/**
 * What has landed that you have not looked at yet.
 *
 * ONE FACT, TWO VIEWS. The badge on a tab and the mark on a row answer the
 * same question — "is there something here I have not seen" — so they read
 * from the same set. An item enters it when the room's websocket says it
 * changed, and leaves it when the row carrying it is actually on your screen.
 * Ageing it by digs or by a timer both failed the real case: a dig runs for
 * minutes, you come back an hour later, and either everything is lit or
 * nothing is.
 *
 * The events are already arriving — the feed is built from them — so this
 * costs a set membership and no fetching.
 */
import type { BoardroomItemKind, BoardroomWebSocketEvent } from '../../types';

export type ReportKey =
  | 'landscape'
  | 'hunches'
  | 'competitors'
  | 'feature_matrix'
  | 'price_map'
  | 'pain_gap'
  | 'demand'
  | 'opportunity_brief';

/**
 * The reports each kind is A ROW OF.
 *
 * NOT "reports it affects". The first version mapped a kind to every table it
 * touched, which made the badge and the marks disagree badly: a dig fired 55
 * events that all mapped to Competitors — companies, their products, their
 * prices, the skeptic's verdicts — so the tab said 55 against a table of 39
 * rows, and the ids that were not company rows lit nothing while the ones that
 * were lit almost everything. A count nobody can point at is worse than none.
 *
 * So a kind appears here only where it has a row it can mark: a PRICING plan
 * is a *cell* of the competitors roster, which the company's row owns, and a
 * cell of the price map, which its product's row owns — it marks the product
 * (see `marksFor`) and nothing on the roster. The cost is that a price landing
 * no longer marks the roster; the gain is that the number on a tab is a number
 * of rows a reader can be taken to.
 */
export const REPORTS_FOR_KIND: Partial<Record<BoardroomItemKind, ReportKey[]>> = {
  // The roster is one row per company; the landscape plots players.
  COMPANY: ['competitors', 'landscape'],
  // A product is a price-map row and a player on the map. In the roster it
  // sits inside its company's row, and in the matrix it is a COLUMN.
  PRODUCT: ['price_map', 'landscape'],
  FEATURE: ['feature_matrix'],
  // A PLAN IS NOT A ROW ANYWHERE — the price map's rows are PRODUCTS, and a
  // plan is one of the prices written across its product's row. So it counts
  // against the PRODUCT it hangs under; see `marksFor`. Counted under its own
  // id, a landed plan put a number on the tab that no row could ever carry.
  PRICING: ['price_map'],
  PAIN: ['pain_gap'],
  HUNCH: ['hunches'],
  IDEA: ['opportunity_brief', 'landscape'],
  // SOLUTION_TYPE groups rows rather than being one; EVIDENCE and the demand
  // lookups feed rows keyed by KEYWORD, which no item id can name. They are
  // real work and they belong in the feed — they are simply not a row here.
};

/** report -> the ids that changed there and have not been seen. */
export type Buckets = Partial<Record<ReportKey, ReadonlySet<number>>>;

/** A row of a report that changed: which table, and WHICH ROW of it. */
export interface Mark {
  report: ReportKey;
  id: number;
}

/** One event's contribution. Returns the SAME object when nothing changed —
 *  including when the item is already unseen — so a dig firing hundreds of
 *  events re-renders the room only when a badge actually moves. */
export function bucket(counts: Buckets, event: BoardroomWebSocketEvent): Buckets {
  const fresh = marksFor(event).filter(m => !counts[m.report]?.has(m.id));
  if (!fresh.length) return counts;
  const next = { ...counts };
  for (const m of fresh) next[m.report] = new Set(next[m.report] ?? []).add(m.id);
  return next;
}

/**
 * The ROWS an event marks: a report, and the id of the row in it.
 *
 * The id is usually the item's own — a company IS a row of the roster — but
 * not always, and the difference is the whole point of the badge: a number on
 * a tab has to name a row somebody can be taken to.
 */
export function marksFor(event: BoardroomWebSocketEvent): Mark[] {
  // A ROW THAT WAS ONLY HANDLED IS NOT NEWS. A seat reserving an item, releasing
  // it, or re-adding one already on the board republishes the row with nothing
  // a reader would see changed, and each of those lit it up again.
  if (event.news === false) return [];
  if (event.status === 'tag') {
    // Approval re-sends the tag it approves: the same placement, stamped.
    if (event.tag?.approved_at) return [];
    const id = event.tag?.item_id;
    return typeof id === 'number' ? [{ report: 'landscape', id }] : [];
  }
  if (event.status !== 'item' || !event.item) return [];
  const item = event.item;
  const marks: Mark[] = [];
  for (const report of REPORTS_FOR_KIND[item.kind] ?? []) {
    // A PLAYER NOBODY HAS PLACED IS NOT ON THE MAP. The landscape's rows are
    // axis values, and a player with no tag on any axis falls in the "not
    // placed on it" total rather than into a row — so counting it put a number
    // on the tab with nothing behind it to look at. A room of 279 players with
    // 122 placed spent most of its badge on rows that do not exist.
    //
    // Nothing is lost by waiting: PLACING one is a tag event, which counts.
    if (report === 'landscape' && !item.tags?.length) continue;
    // A PLAN IS A CELL OF ITS PRODUCT'S ROW. The price map lists products and
    // writes their prices across; a plan has no row of its own, so it marks
    // the product it hangs under — and marks nothing at all until it has one.
    const id =
      report === 'price_map' && item.kind === 'PRICING' ? item.parent_ids?.[0] : item.id;
    if (typeof id === 'number') marks.push({ report, id });
  }
  return marks;
}

/** The item an event is about. A tag names the item it was placed on, which is
 *  the row that changes on the landscape. */
export function itemIdOf(event: BoardroomWebSocketEvent): number | null {
  if (event.status === 'tag') return event.tag?.item_id ?? null;
  if (event.status === 'item') return event.item?.id ?? null;
  return null;
}

/** The reports an event touches. */
export function reportsFor(event: BoardroomWebSocketEvent): ReportKey[] {
  return marksFor(event).map(m => m.report);
}

/**
 * These rows are now on screen: drop them, one report at a time.
 *
 * NOT "the tab was opened". A report can be forty rows long and the three that
 * changed can be at the bottom of it; clearing the badge on arrival would say
 * you had seen work you had not scrolled to. The badge falls as you read.
 */
export function markSeen(
  counts: Buckets,
  report: ReportKey | string,
  ids: readonly number[],
): Buckets {
  const had = counts[report as ReportKey];
  if (!had || !ids.length) return counts;
  const keep = new Set(had);
  let changed = false;
  for (const id of ids) if (keep.delete(id)) changed = true;
  if (!changed) return counts;
  const next = { ...counts };
  if (keep.size) next[report as ReportKey] = keep;
  else delete next[report as ReportKey];
  return next;
}

/**
 * The ids that were INSERTIONS, as opposed to rows that changed.
 *
 * Both light a table up and they want different reactions: a thing that was
 * not there a moment ago, versus a cell of a row you have already read. The
 * server says which — `add_item` is the one place a row is born — and a
 * timestamp could never have told them apart.
 *
 * ONE FLAT SET, not one per report. An item id is unique across the room, so
 * "was #18944 an insertion" has a single answer wherever it is drawn, and
 * keeping it flat is what lets the hue reach every table through context
 * rather than through fifteen props of a display hint.
 */
export function noteFresh(fresh: ReadonlySet<number>, event: BoardroomWebSocketEvent): ReadonlySet<number> {
  if (event.status !== 'item' || event.news === false || !event.fresh) return fresh;
  const id = event.item?.id;
  if (typeof id !== 'number' || fresh.has(id)) return fresh;
  const next = new Set(fresh);
  next.add(id);
  // A backlog, not an archive — the same cap the unseen sets keep.
  return next.size > MAX_UNSEEN
    ? new Set(Array.from(next).slice(-MAX_UNSEEN))
    : next;
}

/** What the tab shows, or "" for nothing. Capped so a long dig cannot widen
 *  the tab bar. */
export function badge(ids: ReadonlySet<number> | undefined): string {
  const n = ids?.size ?? 0;
  if (!n) return '';
  return n > 99 ? '99+' : String(n);
}

// ------------------------------ persistence ------------------------------
//
// Unseen means unseen tomorrow too. A dig finishes while the tab is closed and
// the whole point is that its work is still marked when you come back.

const KEY = (roomId: number) => `jcseo_boardroom_unseen_${roomId}`;

/** Ids per report, capped: an unseen set is a backlog, not an archive. */
export const MAX_UNSEEN = 500;

export function loadBuckets(roomId: number): Buckets {
  try {
    const raw = window.localStorage.getItem(KEY(roomId));
    if (!raw) return {};
    const obj = JSON.parse(raw) as Record<string, number[]>;
    const out: Buckets = {};
    for (const [k, ids] of Object.entries(obj)) {
      if (Array.isArray(ids) && ids.length) out[k as ReportKey] = new Set(ids.slice(-MAX_UNSEEN));
    }
    return out;
  } catch {
    return {};
  }
}

/** Forget every unseen mark for a room — for when its items are gone, so no
 *  badge or glow is left pointing at a row that no longer exists. */
export function clearBuckets(roomId: number): void {
  try {
    window.localStorage.removeItem(KEY(roomId));
  } catch {
    /* nothing stored, nothing to forget */
  }
}

export function saveBuckets(roomId: number, counts: Buckets): void {
  try {
    const obj: Record<string, number[]> = {};
    for (const [k, ids] of Object.entries(counts)) {
      if (ids && ids.size) obj[k] = Array.from(ids).slice(-MAX_UNSEEN);
    }
    window.localStorage.setItem(KEY(roomId), JSON.stringify(obj));
  } catch {
    /* a viewer who blocks site data simply starts each session unmarked */
  }
}
